import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, TrendingDown, Wallet, CreditCard,
  Plus, X, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { useWorkflowStore } from '../store/workflowStore';
import { formatDate } from '../utils/helpers';
import { toast } from '../components/Toast';

const fmtCurrency = (n: number) =>
  `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinancePage() {
  const {
    financeTransactions, addFinanceTransaction,
    getTotalIncome, getTotalExpenses, getTotalMaterialCost, getNetProfit,
    invoices,
  } = useWorkflowStore();

  const [activeTab, setActiveTab] = useState<'overview' | 'profit-splitter' | 'records'>('overview');
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [newRecord, setNewRecord] = useState({
    type: 'EXPENSE' as 'INCOME' | 'EXPENSE',
    category: '',
    amount: 0,
    description: '',
    reference: '',
  });

  // ── Real Computed Totals ──────────────────────────────────────────────
  const totalIncome    = getTotalIncome();
  const totalExpenses  = getTotalExpenses();
  const materialCost   = getTotalMaterialCost();
  const netProfit      = getNetProfit();

  // Monthly chart data from real transactions
  const monthlyData = useMemo(() => {
    const now = new Date();
    const result = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const month = d.getMonth();
      const year  = d.getFullYear();
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const rev = financeTransactions
        .filter(t => { const dt = new Date(t.date); return dt.getMonth() === month && dt.getFullYear() === year && t.type === 'INCOME'; })
        .reduce((s, t) => s + t.amount, 0);
      const exp = financeTransactions
        .filter(t => { const dt = new Date(t.date); return dt.getMonth() === month && dt.getFullYear() === year && t.type === 'EXPENSE'; })
        .reduce((s, t) => s + t.amount, 0);
      result.push({ month: monthNames[month], revenue: rev, expenses: exp });
    }
    return result;
  }, [financeTransactions]);

  const maxVal = Math.max(...monthlyData.map(d => Math.max(d.revenue, d.expenses)), 1);

  // ── Profit Splitter ──────────────────────────────────────────────────
  const [splitter, setSplitter] = useState({ ownerSalary: 40, businessOperations: 30, emergencyFund: 10, toolsUpgrade: 20 });
  const splitterTotal = splitter.ownerSalary + splitter.businessOperations + splitter.emergencyFund + splitter.toolsUpgrade;
  const splitterValid = splitterTotal === 100;

  const updateSplitter = (key: keyof typeof splitter, value: number) => {
    setSplitter(prev => ({ ...prev, [key]: value }));
  };

  const categories = ['Staff Salary', 'Office Rent', 'Fuel & Transport', 'Inventory Purchase', 'Utilities', 'Marketing', 'Maintenance', 'CCTV Installation', 'Smart Home', 'CCTV Sales', 'Starlink Installation', 'Consultation', 'Other'];

  const handleAddRecord = () => {
    if (!newRecord.category) { toast.error('Validation Error', 'Please select a category.'); return; }
    if (newRecord.amount <= 0) { toast.error('Validation Error', 'Amount must be greater than 0.'); return; }
    if (!newRecord.description.trim()) { toast.error('Validation Error', 'Description is required.'); return; }
    addFinanceTransaction({
      type: newRecord.type,
      amount: newRecord.amount,
      description: newRecord.description,
      date: new Date().toISOString(),
      category: newRecord.category,
      reference: newRecord.reference,
    });
    toast.success('Record Added', `${newRecord.type === 'INCOME' ? 'Income' : 'Expense'} of ${fmtCurrency(newRecord.amount)} recorded.`);
    setNewRecord({ type: 'EXPENSE', category: '', amount: 0, description: '', reference: '' });
    setShowAddRecord(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Finance</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Revenue, expenses & profit management</p>
        </div>
        <button onClick={() => setShowAddRecord(true)} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
          <Plus className="w-4 h-4" />Add Record
        </button>
      </div>

      {/* Tab Bar */}
      <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden w-fit">
        {(['overview', 'profit-splitter', 'records'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 h-10 text-[13px] font-medium capitalize transition-all ${activeTab === tab ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
            {tab.replace('-', ' ')}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-[13px] font-medium text-gray-500">Total Revenue Received</span>
              </div>
              <div className="text-[22px] font-bold text-gray-900">{fmtCurrency(totalIncome)}</div>
              <div className="flex items-center gap-1 mt-1 text-[12px] text-emerald-600 font-medium">
                <ArrowUpRight className="w-3 h-3" />From received payments only
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-white" />
                </div>
                <span className="text-[13px] font-medium text-gray-500">Total Expenses</span>
              </div>
              <div className="text-[22px] font-bold text-gray-900">{fmtCurrency(totalExpenses)}</div>
              <div className="flex items-center gap-1 mt-1 text-[12px] text-red-500 font-medium">
                <ArrowDownRight className="w-3 h-3" />Recorded operational expenses
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <span className="text-[13px] font-medium text-gray-500">Material Cost</span>
              </div>
              <div className="text-[22px] font-bold text-gray-900">{fmtCurrency(materialCost)}</div>
              <div className="text-[12px] text-gray-400 mt-1">Product cost × quantity (internal)</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <span className="text-[13px] font-medium text-gray-500">Net Profit</span>
              </div>
              <div className={`text-[22px] font-bold ${netProfit >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{fmtCurrency(netProfit)}</div>
              <div className="text-[12px] text-gray-400 mt-1">Revenue − Expenses − Material Cost</div>
            </motion.div>
          </div>

          {/* Monthly Chart */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[15px] font-semibold text-gray-900">Monthly Overview</h3>
              <div className="flex items-center gap-4 text-[12px]">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#7FA86A] inline-block" />Revenue</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" />Expenses</span>
              </div>
            </div>
            {monthlyData.every(d => d.revenue === 0 && d.expenses === 0) ? (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                No transactions yet. Finance data appears after recording invoice payments.
              </div>
            ) : (
              <div className="flex items-end gap-4 h-48">
                {monthlyData.map((d) => (
                  <div key={d.month} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex gap-1 items-end justify-center" style={{ height: '160px' }}>
                      <div className="w-5 rounded-t-lg bg-gradient-to-t from-[#7FA86A] to-[#96BE82] transition-all duration-500"
                        style={{ height: `${maxVal > 0 ? (d.revenue / maxVal) * 100 : 0}%`, minHeight: d.revenue > 0 ? '4px' : '0' }} />
                      <div className="w-5 rounded-t-lg bg-gray-200/80 transition-all duration-500"
                        style={{ height: `${maxVal > 0 ? (d.expenses / maxVal) * 100 : 0}%`, minHeight: d.expenses > 0 ? '4px' : '0' }} />
                    </div>
                    <span className="text-[11px] text-gray-400 font-medium">{d.month}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Profit Breakdown */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
            <h3 className="text-[15px] font-semibold text-gray-900 mb-4">Profit Breakdown</h3>
            <div className="space-y-3 text-[13px]">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">Revenue Received (Cash In)</span>
                <span className="font-semibold text-emerald-600">{fmtCurrency(totalIncome)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">− Operational Expenses</span>
                <span className="font-semibold text-red-500">({fmtCurrency(totalExpenses)})</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-500">− Material Cost (Products)</span>
                <span className="font-semibold text-blue-500">({fmtCurrency(materialCost)})</span>
              </div>
              <div className="flex justify-between items-center py-2 font-bold text-base">
                <span>= Net Profit</span>
                <span className={netProfit >= 0 ? 'text-[#7FA86A]' : 'text-red-600'}>{fmtCurrency(netProfit)}</span>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {activeTab === 'profit-splitter' && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-[18px] font-semibold text-gray-900">Profit Splitter</h3>
              <p className="text-[13px] text-gray-400 mt-0.5">Allocate net profit across 4 virtual bank cards</p>
            </div>
            <div className={`px-3 py-1.5 rounded-full text-[12px] font-semibold ${splitterValid ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
              {splitterTotal}% / 100%
            </div>
          </div>

          <div className="mb-4 p-3 bg-gray-50 rounded-xl text-[13px]">
            <span className="text-gray-500">Splitting: </span>
            <span className="font-bold text-[#7FA86A]">{fmtCurrency(netProfit)}</span>
            <span className="text-gray-400 ml-2">(Net Profit)</span>
            {netProfit <= 0 && (
              <span className="ml-2 text-red-500 text-[12px]">— No profit to split yet</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {[
              { key: 'ownerSalary' as const, label: 'Owner Salary', color: 'from-[#7FA86A] to-[#96BE82]', icon: '💰' },
              { key: 'businessOperations' as const, label: 'Business Operations', color: 'from-blue-400 to-blue-600', icon: '🏢' },
              { key: 'emergencyFund' as const, label: 'Emergency Fund', color: 'from-emerald-400 to-emerald-600', icon: '🛡️' },
              { key: 'toolsUpgrade' as const, label: 'Tools Upgrade', color: 'from-violet-400 to-violet-600', icon: '🔧' },
            ].map((card, idx) => (
              <div key={card.key} className="relative bg-white border border-gray-200/60 rounded-2xl p-5 overflow-hidden group hover:shadow-lg transition-all">
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.color}`} />
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{card.icon}</span>
                  <div>
                    <h4 className="text-[14px] font-semibold text-gray-900">{card.label}</h4>
                    <p className="text-[12px] text-gray-400">Card {String(idx + 1).padStart(2, '0')}</p>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[22px] font-bold text-gray-900">
                      {splitterValid ? fmtCurrency(netProfit * (splitter[card.key] / 100)) : '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="100" value={splitter[card.key]}
                      onChange={(e) => updateSplitter(card.key, Math.min(100, Math.max(0, Number(e.target.value))))}
                      className="w-16 h-9 text-right text-[14px] font-semibold px-2 rounded-lg bg-gray-50 border border-gray-200/60 focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
                    <span className="text-[14px] text-gray-400">%</span>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${card.color} transition-all duration-300`}
                    style={{ width: `${splitter[card.key]}%` }} />
                </div>
              </div>
            ))}
          </div>

          {!splitterValid && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 rounded-xl text-[13px] text-red-600 font-medium">
              <X className="w-4 h-4" />Total must equal 100%. Currently at {splitterTotal}%
            </div>
          )}
          {splitterValid && (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 rounded-xl text-[13px] text-emerald-600 font-medium">
              ✅ Distribution valid — total is exactly 100%
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'records' && (
        <div className="space-y-3">
          {financeTransactions.length === 0 && (
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-12 text-center">
              <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-[14px] font-semibold text-gray-500">No finance records yet</p>
              <p className="text-[12px] text-gray-400 mt-1">Income records are created automatically when invoice payments are recorded.</p>
            </div>
          )}
          {financeTransactions.map((record, i) => (
            <motion.div key={record.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-4 flex items-center gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${record.type === 'INCOME' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                {record.type === 'INCOME'
                  ? <TrendingUp className="w-5 h-5 text-emerald-500" />
                  : <TrendingDown className="w-5 h-5 text-red-500" />}
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-medium text-gray-900">{record.description}</div>
                <div className="text-[12px] text-gray-400">
                  {record.category || record.type} · {record.customerName ? `${record.customerName} · ` : ''}
                  {record.date ? new Date(record.date).toLocaleDateString('en-LK') : ''}
                </div>
                {record.invoiceNumber && (
                  <div className="text-[11px] text-blue-500">{record.invoiceNumber}</div>
                )}
              </div>
              <div className={`text-[14px] font-semibold ${record.type === 'INCOME' ? 'text-emerald-600' : 'text-red-500'}`}>
                {record.type === 'INCOME' ? '+' : '-'}{fmtCurrency(record.amount)}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Record Modal */}
      {showAddRecord && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddRecord(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">Add Finance Record</h2>
              <button onClick={() => setShowAddRecord(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Type</label>
                <div className="flex gap-2">
                  {(['INCOME', 'EXPENSE'] as const).map(t => (
                    <button key={t} onClick={() => setNewRecord({ ...newRecord, type: t })}
                      className={`flex-1 h-10 rounded-xl text-[13px] font-medium capitalize transition-all ${newRecord.type === t ? (t === 'INCOME' ? 'bg-emerald-50 text-emerald-600 border-2 border-emerald-200' : 'bg-red-50 text-red-500 border-2 border-red-200') : 'bg-gray-50 text-gray-500 border border-gray-200/60'}`}>
                      {t.charAt(0) + t.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Category *</label>
                <select value={newRecord.category} onChange={e => setNewRecord({ ...newRecord, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5">
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Amount (Rs.) *</label>
                <input type="number" value={newRecord.amount || ''} onChange={e => setNewRecord({ ...newRecord, amount: Number(e.target.value) })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Description *</label>
                <input value={newRecord.description} onChange={e => setNewRecord({ ...newRecord, description: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddRecord(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleAddRecord} disabled={!newRecord.category || newRecord.amount <= 0 || !newRecord.description}
                className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 disabled:opacity-50">Add Record</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
